/**
 * BoardPriceModal — append a row to a board's price list and show its history.
 *
 * Shared by the Board Catalog (super admin) and the Hardware Provider detail
 * page (super admin or that provider's manufacturer admin). Prices are never
 * edited in place: saving inserts a new board_prices row with an
 * effective_from, and the latest one not in the future is the price in force.
 * Amounts are typed in major units and sent as integer cents.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconX } from '@tabler/icons-react';
import { getBoardPrices, setBoardPrice, getVariantPrices, setVariantPrice, boardLabel, formatMoney } from '../services/deviceBoards';
import type { DeviceBoard, BoardPrice, BoardPriceInput } from '../services/deviceBoards';
import { getMarketplaceSettings, formatBps } from '../services/platformSettings';
import {
  Button,
  StatusChip,
  MutedText,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  HelperText,
  ErrorAlert,
  FormSectionTitle,
  H2Title,
} from './styled';

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.font.size.sm};
  margin-top: ${({ theme }) => theme.spacing(2)};

  th, td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(1, 2)};
    border-bottom: 1px solid ${({ theme }) => theme.border.color.light};
  }

  th {
    color: ${({ theme }) => theme.font.color.tertiary};
    font-weight: ${({ theme }) => theme.font.weight.medium};
  }
`;

interface PriceForm {
  currency: string;
  hardware_price: string; // major units as typed ("249.00")
  monthly_fee: string;
  effective_from: string; // datetime-local value or ''
  note: string;
}

const priceFormFrom = (price: BoardPrice | null | undefined): PriceForm => ({
  currency: price?.currency || 'USD',
  hardware_price: price ? (price.hardware_price_cents / 100).toFixed(2) : '',
  monthly_fee: price ? (price.monthly_fee_cents / 100).toFixed(2) : '',
  effective_from: '',
  note: '',
});

function toCents(major: string): number {
  // Coerce before sending: '' → 0, and round so "12.345" can't slip through as a float.
  if (major === '' || major === null) return 0;
  return Math.round(Number(major) * 100);
}

function toPriceInput(form: PriceForm): BoardPriceInput {
  return {
    currency: form.currency.trim().toUpperCase(),
    hardware_price_cents: toCents(form.hardware_price),
    monthly_fee_cents: toCents(form.monthly_fee),
    effective_from: form.effective_from ? new Date(form.effective_from).toISOString() : null,
    note: form.note.trim() || null,
  };
}

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

interface BoardPriceModalProps {
  board: DeviceBoard;
  /** When set, prices this SKU instead of the board-level family default. */
  variant?: { id: number; sku: string; name: string; current_price?: BoardPrice | null } | null;
  onClose: () => void;
  /** Called after a successful save (the modal closes itself). */
  onSaved?: () => void;
}

export function BoardPriceModal({ board, variant, onClose, onSaved }: BoardPriceModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PriceForm>(() => priceFormFrom(variant ? variant.current_price : board.current_price));
  const [error, setError] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['marketplace-settings'],
    queryFn: getMarketplaceSettings,
  });
  const { data: history } = useQuery({
    queryKey: variant ? ['variant-prices', board.id, variant.id] : ['board-prices', board.id],
    queryFn: () => (variant ? getVariantPrices(board.id, variant.id) : getBoardPrices(board.id)),
  });

  const mutation = useMutation({
    mutationFn: (data: BoardPriceInput) => (variant
      ? setVariantPrice(board.id, variant.id, data)
      : setBoardPrice(board.id, data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-boards'] });
      queryClient.invalidateQueries({ queryKey: ['board-prices', board.id] });
      if (variant) queryClient.invalidateQueries({ queryKey: ['variant-prices', board.id, variant.id] });
      queryClient.invalidateQueries({ queryKey: ['board-variants-priced', board.id] });
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['manufacturer'] });
      onSaved?.();
      onClose();
    },
    onError: (err: any) => {
      const body = err.response?.data;
      setError(body?.details?.join?.('; ') || body?.error || 'Failed to set price');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(toPriceInput(form));
  };

  const cutLabel = settings ? formatBps(settings.platform_fee_bps) : null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>{variant ? `Price ${variant.sku}` : `Price ${boardLabel(board)}`}</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close">
            <IconX size={18} stroke={2} />
          </ModalCloseButton>
        </ModalHeader>
        <form onSubmit={submit}>
          <ModalBody>
            {error && <ErrorAlert>{error}</ErrorAlert>}
            <MutedText>
              Prices are never edited in place — saving adds a new entry to the
              {variant ? ` ${variant.sku}` : " board's"} price list, in force from the date you choose (now by default).
              {cutLabel && ` The platform cut of ${cutLabel} is taken out of these amounts.`}
            </MutedText>
            {variant?.current_price?.inherited && (
              <MutedText>
                {variant.sku} currently inherits the board's family default price —
                saving here gives it a price of its own.
              </MutedText>
            )}

            <FieldGrid>
              <FormGroup>
                <Label>Hardware price (per unit)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.hardware_price}
                  onChange={(e) => setForm({ ...form, hardware_price: e.target.value })}
                  placeholder="249.00"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Monthly service fee (per deployed device)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.monthly_fee}
                  onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                  placeholder="5.00"
                  required
                />
              </FormGroup>
            </FieldGrid>

            <FieldGrid>
              <FormGroup>
                <Label>Currency</Label>
                <Input
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  maxLength={3}
                  pattern="[A-Za-z]{3}"
                  required
                />
                <HelperText>3-letter ISO code.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label>Effective from</Label>
                <Input
                  type="datetime-local"
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                />
                <HelperText>Leave blank to apply immediately.</HelperText>
              </FormGroup>
            </FieldGrid>

            <FormGroup>
              <Label>Note</Label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Why this change (optional)"
              />
            </FormGroup>

            {history && history.prices.length > 0 && (
              <>
                <FormSectionTitle>History</FormSectionTitle>
                <HistoryTable>
                  <thead>
                    <tr>
                      <th>Effective from</th>
                      <th>Hardware</th>
                      <th>Monthly</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.prices.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {formatWhen(p.effective_from)}
                          {history.current?.id === p.id && <> <StatusChip variant="positive">current</StatusChip></>}
                          {new Date(p.effective_from) > new Date() && <> <StatusChip variant="neutral">scheduled</StatusChip></>}
                        </td>
                        <td>{formatMoney(p.hardware_price_cents, p.currency)}</td>
                        <td>{formatMoney(p.monthly_fee_cents, p.currency)}</td>
                        <td>{p.note || <MutedText>—</MutedText>}</td>
                      </tr>
                    ))}
                  </tbody>
                </HistoryTable>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save price'}
            </Button>
          </ModalFooter>
        </form>
      </ModalCard>
    </ModalOverlay>
  );
}

export default BoardPriceModal;
